/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { useEffect } from 'react';
import { DefinitionTooltip, Link } from '@carbon/react';

import { editProcessApplicationVersion } from 'App/Pages/ProcessApplicationVersions/features/edit';
import { Review, useInitReviewStore } from 'App/Pages/ProcessApplicationVersions/features/review';
import { VersionsList, DRAFT_VERSION, DraftVersion } from 'components';
import { organizationStore, userStore } from 'stores';
import { VersionMenu } from 'App/Pages/ProcessApplicationVersions/features/view-versions-list/version/version-menu';
import { openVersionCreationModal, VersionCreationModal } from 'features/create-process-app-version';

import { EmptyState } from './empty-state';
import { useSelectDraftIfSelectedVersionIsDeleted } from './use-select-draft-if-selected-version-is-deleted';

export default function ProcessApplicationVersionsList({
  versions,
  reloadVersions,
  processApplication,
  selectedVersionId,
  setSelectedVersionId,
  isDiffingEnabled
}) {
  useEffect(() => {
    setSelectedVersionId(DRAFT_VERSION.id);
  }, []);

  useSelectDraftIfSelectedVersionIsDeleted(versions, selectedVersionId, setSelectedVersionId, DRAFT_VERSION);

  const newestVersion = versions[0];

  useInitReviewStore({
    versions
  });

  const renderVersionMenu = ({ version, setIsEditing, isCurrentUser }) => (
    <VersionMenu
      versionId={version.id}
      versionName={version.name}
      processApplicationName={processApplication.name}
      reloadVersions={reloadVersions}
      setIsEditing={setIsEditing}
      isVersionByCurrentUser={isCurrentUser}
      hasElevatedOrganizationPermissions={organizationStore.hasElevatedOrganizationPermissions}
    />
  );

  const renderReview = ({ version }) => {
    const { createdBy } = version;
    const authorId = createdBy.id;

    return (
      <Review
        versionId={version.id}
        isNewestVersion={newestVersion.id === version.id}
        currentUserId={userStore.userId}
        versionCreatorUserId={authorId}
        hasElevatedOrganizationPermissions={organizationStore.hasElevatedOrganizationPermissions}
      />
    );
  };

  const editVersion = async ({ name, description, version, reloadVersions, setIsEditing }) =>
    await editProcessApplicationVersion(version.id, name, reloadVersions, setIsEditing, description);

  return (
    <VersionsList
      versions={versions}
      reloadVersions={reloadVersions}
      selectedVersionId={selectedVersionId}
      setSelectedVersionId={(version) => setSelectedVersionId(version.id)}
      isDiffingEnabled={isDiffingEnabled}
      currentUserId={userStore.userId}
      DraftVersion={
        <DraftVersion
          lastModified={processApplication?.files[0]?.changed}
          isPrimarySelection={selectedVersionId === DRAFT_VERSION.id}
          isSecondarySelection={false}
          setSelectedVersionId={setSelectedVersionId}
          onCreateVersion={openVersionCreationModal}
          VersionCreationModal={
            <VersionCreationModal processApplicationId={processApplication?.id} onVersionCreated={reloadVersions} />
          }
        />
      }
      EmptyState={<EmptyState isNoVersionsPresent={versions?.length === 0} />}
      editDialogLabel={
        <DefinitionTooltip
          autoAlign
          definition={
            <span role="tooltip">
              Version tags represent a snapshot of the process application. To ensure version integrity, tags cannot be
              edited.
            </span>
          }
          openOnHover
          align="right"
        >
          <label id="version-tag-label">Version tag</label>
        </DefinitionTooltip>
      }
      isNameFieldEditable={false}
      editDialogSubtitle={
        <>
          Use versioning to track and review changes across all resources in the process application.&nbsp;
          <Link
            href="https://docs.camunda.io/docs/next/components/modeler/web-modeler/process-application-versioning/"
            target="_blank"
          >
            Learn more
          </Link>
        </>
      }
      renderVersionMenu={renderVersionMenu}
      renderReview={renderReview}
      onEditVersion={editVersion}
    />
  );
}
